import { Link, useRouterState } from '@tanstack/react-router'
import {
  Boxes,
  KeySquare,
  LayoutList,
  PanelLeftClose,
  PanelRightClose,
  Settings,
  Webhook,
} from 'lucide-react'
import { type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { BrandMark } from '@/components/brand-mark'
import { cn } from '@/lib/utils'

type NavItem = {
  to: string
  label: string
  icon: LucideIcon
  description: string
  matchPrefix?: boolean
}

export const NAV: readonly NavItem[] = [
  {
    to: '/runs',
    label: 'Runs',
    icon: LayoutList,
    description: 'Live harness runs',
    matchPrefix: true,
  },
  {
    to: '/templates',
    label: 'Templates',
    icon: Boxes,
    description: 'Cached harness templates',
  },
  {
    to: '/env-vault',
    label: 'Env Vault',
    icon: KeySquare,
    description: 'Per-repo secrets',
  },
  {
    to: '/webhooks',
    label: 'Webhooks',
    icon: Webhook,
    description: 'Lifecycle event subscribers',
    matchPrefix: true,
  },
  {
    to: '/settings',
    label: 'Settings',
    icon: Settings,
    description: 'API token & theme',
  },
] as const

type SidebarProps = {
  collapsed: boolean
  onToggle: () => void
  onItemClick?: () => void
  variant?: 'desktop' | 'drawer'
}

export function Sidebar({ collapsed, onToggle, onItemClick, variant = 'desktop' }: SidebarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isDrawer = variant === 'drawer'
  const showLabels = !collapsed || isDrawer

  return (
    <aside
      data-collapsed={collapsed && !isDrawer}
      className={cn(
        'flex h-full flex-col bg-sidebar text-sidebar-foreground',
        isDrawer ? 'w-full' : 'border-r border-sidebar-border',
        !isDrawer && (collapsed ? 'w-16' : 'w-60'),
      )}
    >
      <div
        className={cn(
          'flex h-14 items-center border-b border-sidebar-border',
          showLabels ? 'px-4' : 'px-0 justify-center',
        )}
      >
        <Link
          to="/runs"
          className="inline-flex items-center gap-2 outline-none rounded-md focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          onClick={onItemClick}
          aria-label="Litmers Agent Control"
        >
          <BrandMark collapsed={!showLabels} />
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 scrollbar-thin">
        <ul className="space-y-0.5">
          {NAV.map((item) => {
            const active = item.matchPrefix
              ? pathname === item.to || pathname.startsWith(`${item.to}/`)
              : pathname === item.to
            const Icon = item.icon
            const linkInner = (
              <Link
                to={item.to}
                onClick={onItemClick}
                data-active={active}
                className={cn(
                  'group relative flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium outline-none transition-colors',
                  'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  'focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                  active && 'bg-sidebar-accent text-sidebar-accent-foreground',
                  !showLabels && 'justify-center px-0',
                )}
              >
                {active ? (
                  <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-sidebar-primary" />
                ) : null}
                <Icon
                  className={cn(
                    'size-[18px] shrink-0',
                    active ? 'text-sidebar-primary' : 'text-muted-foreground group-hover:text-foreground',
                  )}
                />
                {showLabels ? (
                  <span className="truncate">{item.label}</span>
                ) : (
                  <span className="sr-only">{item.label}</span>
                )}
              </Link>
            )
            if (!showLabels) {
              return (
                <li key={item.to}>
                  <Tooltip>
                    <TooltipTrigger asChild>{linkInner}</TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      <div>{item.label}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {item.description}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </li>
              )
            }
            return <li key={item.to}>{linkInner}</li>
          })}
        </ul>
      </nav>

      {!isDrawer ? (
        <div className="border-t border-sidebar-border p-2">
          <Button
            variant="ghost"
            size={collapsed ? 'icon-sm' : 'sm'}
            onClick={onToggle}
            className={cn(
              'text-muted-foreground hover:text-foreground',
              collapsed ? 'mx-auto' : 'w-full justify-start',
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelRightClose /> : <PanelLeftClose />}
            {!collapsed ? <span className="ml-2">Collapse</span> : null}
          </Button>
        </div>
      ) : null}
    </aside>
  )
}
